import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { formatClubDateTime } from '../../utils/clubTime';

const client = generateClient({ authMode: 'userPool' });

const ACTION_LABELS: Record<string, string> = {
  MEMBER_APPROVED: 'Member Approved',
  MEMBER_REJECTED: 'Member Rejected',
  MEMBER_DELETED: 'Member Deleted',
  MEMBER_EDITED: 'Member Edited',
  MEMBER_RENEWED: 'Member Renewed',
  FEE_ADJUSTED: 'Fee Adjusted',
  PAYMENT_RECEIVED: 'Payment Received',
  PAYMENT_REVERSED: 'Payment Reversed',
  ADMIN_PROMOTED: 'Admin Promoted',
  ADMIN_REMOVED: 'Admin Removed',
  EVENT_CREATED: 'Event Created',
  EVENT_UPDATED: 'Event Updated',
  CATCH_APPROVED: 'Catch Approved',
  CATCH_DISQUALIFIED: 'Catch Disqualified',
  COMPETITION_STATE_CHANGED: 'Competition State Changed',
  ACTIVATION_EMAIL_SENT: 'Activation Email Sent',
  PAYMENT_LINK_SENT: 'Payment Link Sent',
  NOTIFICATION_SENT: 'Notification Sent',
  BULK_IMPORT: 'Bulk Import',
};

const AuditLogViewer: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('ALL');
  // #157 (S4): how many of the (newest-first) loaded rows to actually show.
  // Starts at 200 and grows by 200 per "Load more" click, up to logs.length.
  const [visibleCount, setVisibleCount] = useState(200);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    setVisibleCount(200);
    try {
      // #157 (S4): this used to be a single list({ limit: 200 }) call, which
      // returns an ARBITRARY 200 rows (whatever DynamoDB's scan happens to
      // read first) rather than the 200 newest. No secondary index here
      // sorts "all" rows newest-first (adminSub/actionType/targetEntityId
      // each sort by timestamp, but only within that one admin/action/
      // entity) so there is no index query that gives a global
      // newest-first order. Page through every row with nextToken instead,
      // then sort client-side by timestamp descending.
      let all: any[] = [];
      let nextToken: string | null | undefined;
      do {
        const page: any = await client.models.AuditLog.list({
          limit: 1000,
          nextToken: nextToken || undefined,
        });
        all = all.concat(page.data || []);
        nextToken = page.nextToken;
      } while (nextToken);

      const sorted = all.sort(
        (a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      setLogs(sorted);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = actionFilter === 'ALL'
    ? logs
    : logs.filter((l: any) => l.actionType === actionFilter);

  const visible = filtered.slice(0, visibleCount);

  const uniqueActions = Array.from(new Set(logs.map((l: any) => l.actionType))).sort();

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading audit log…</div>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); setVisibleCount(200); }}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          aria-label="Filter by action type"
        >
          <option value="ALL">All Actions</option>
          {uniqueActions.map((a) => (
            <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
          ))}
        </select>
        <span role="status" className="text-sm text-gray-500">
          {filtered.length > visible.length
            ? `Showing the newest ${visible.length} of ${filtered.length}`
            : `${filtered.length} entries`}
        </span>
        <button
          onClick={fetchLogs}
          className="ml-auto px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
          No audit log entries found.
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admin</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {visible.map((log: any) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {formatClubDateTime(log.timestamp)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      {log.adminEmail}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {ACTION_LABELS[log.actionType] || log.actionType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-md truncate">
                      {log.description}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {log.targetEntityType && (
                        <span>{log.targetEntityType} {log.targetEntityId ? `#${log.targetEntityId.slice(0, 8)}` : ''}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {visible.length < filtered.length && (
            <div className="p-3 text-center border-t border-gray-200">
              <button
                onClick={() => setVisibleCount((c) => c + 200)}
                className="px-4 py-2 min-h-[44px] text-sm font-medium text-fishing-blue border border-fishing-blue rounded-md hover:bg-blue-50"
              >
                Load more
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditLogViewer;
